import { CustomDropDown, Logo } from 'Components';
import { useAppDispatch, useAppSelector, useT } from 'Config';
import { API_URL } from 'Env';
import { authStore } from 'Features/Authentication/lib';
import { attemptLogout } from 'Features/Authentication/lib/actions';
import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { ScaleIconUserFileUser } from '@telekom/scale-components-react';
import { useLocation } from 'react-router';
import { ROLE_ADMIN, ROLE_EDITOR, ROLE_SUPER_ADMIN } from 'Components/constants';
import { useUserHasRights } from 'Components/hooks/useUserHasRights';
import classNames from 'classnames';
import { Link, matchPath, NavLink } from 'react-router-dom';

export const hideBrandBarWidth = 800;

export function AppHeader() {
  const t = useT();

  const location = useLocation();
  const SCROLL_THESHOLD = 50;
  const user = useAppSelector(authStore.selectUser);
  const [isSmall, setIsSmall] = useState(document.documentElement.clientWidth < hideBrandBarWidth);
  const dispatch = useAppDispatch();
  const hasRights = useUserHasRights();

  const logout = useCallback(() => {
    dispatch(attemptLogout(true)).finally(() => window.location.reload());
  }, [dispatch]);

  useEffect(() => {
    const handleScroll = () => {
      const node = window.scrollY;
      const forceHide = document.documentElement.clientWidth < hideBrandBarWidth;
      if (node > SCROLL_THESHOLD || forceHide) {
        setIsSmall(true);
      } else if (node < SCROLL_THESHOLD) {
        setIsSmall(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useLayoutEffect(() => {
    let i: NodeJS.Timeout;
    function updateSize() {
      const forceHide = document.documentElement.clientWidth < hideBrandBarWidth;
      clearTimeout(i);
      i = setTimeout(() => {
        setIsSmall(forceHide ? true : window.scrollY > 15);
      }, 100);
    }
    window.addEventListener('resize', updateSize);
    updateSize();
    return () => {
      clearTimeout(i);
      window.removeEventListener('resize', updateSize);
    };
  }, []);

  const isHomePage = Boolean(matchPath(location.pathname, '/'));

  return (
    <>
      <header className={classNames('relative z-[11] w-full', 'flex flex-shrink-0 basis-auto', isSmall ? 'h-[9.5rem]' : 'h-44')}>
        <div className={classNames('fixed top-0', 'duration-200 ease-out', isSmall ? 'h-[4.5rem]' : 'h-24', 'w-full bg-white')}>
          <div className="page flex h-full flex-row justify-between gap-x-4 items-center">
            <div className="flex h-full flex-row items-center justify-start gap-10 sm:gap-16">
              <Link
                to="/"
                className={classNames('flex h-full items-center justify-center bg-primary-500', isSmall ? 'px-6 py-5' : 'px-7 py-6')}
                aria-label={t.global.to_home_page}
                title={t.global.to_home_page}
              >
                <Logo className={classNames('h-full text-white')} />
              </Link>

              {/* LeftSectorNavigation  */}
              <div className={classNames('container', 'flex h-full min-w-0 flex-col')}>
                <nav className="relative flex flex-1">
                  <ul className="text-header-primary ml-[-1em] flex items-center gap-4 sm:gap-8" role="menu">
                    <li className={classNames('pb-1', 'h-full', 'relative')}>
                      <NavLink
                        className={classNames(
                          'mt-1',
                          'text-xl !font-extrabold !no-underline',
                          'flex h-full items-center justify-center',
                          'relative whitespace-nowrap border-b-4 border-solid border-transparent pt-1',
                          'hover:border-b-primary-800 hover:text-primary-500  focus:border-b-primary-800 focus:text-primary-500',
                          isHomePage ? 'border-b-primary-500 text-primary-500' : ''
                        )}
                        title={t.appHeader.overview_all_campaigns}
                        aria-label={t.appHeader.overview_all_campaigns}
                        to="/"
                      >
                        {t.appHeader.overview}
                      </NavLink>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>

            <div className="flex h-full flex-row items-center justify-start gap-4 sm:gap-6">
              {/* RightSectorNavigation */}
              <CustomDropDown
                label=""
                {...('/profile' === location.pathname && { classes: 'active' })}
                showChevron={false}
                itemsLabel={`${user?.first_name} ${user?.last_name}`}
                Icon={<ScaleIconUserFileUser size={30} />}
                items={[
                  { label: t.appHeader.dropDownOptions.profile, href: '/profile' },
                  ...(hasRights ? [{ label: t.appHeader.dropDownOptions.my_space, href: '/dashboard' }] : []),
                  ...(user?.role.toLowerCase() === ROLE_SUPER_ADMIN ||
                  user?.role.toLowerCase() === ROLE_ADMIN ||
                  user?.role.toLowerCase() === ROLE_EDITOR
                    ? [
                        {
                          label: t.appHeader.dropDownOptions.admin,
                          href: `${API_URL || ''}`.split('/').slice(0, -3).join('/') + '/admin',
                          newTab: true,
                        },
                      ]
                    : []),
                  { label: t.appHeader.dropDownOptions.logOut, onSelect: logout },
                ]}
              />
            </div>
          </div>
        </div>
        <div
          className={classNames(
            'z-10',
            'bg-white/80 backdrop-blur',
            'w-full',
            'flex flex-shrink-0 basis-auto',
            'fixed',
            'duration-200 ease-out',
            isSmall ? 'top-[4.5rem]' : 'top-[6rem]',
            isSmall ? 'h-10' : 'h-20',
            'border-t border-gray-200',
            'shadow-md'
          )}
        >
          <div
            className={classNames(
              'page inline-flex w-full flex-row items-center justify-start gap-1',
              'tracking-tight text-primary-500',
              isSmall ? 'text-2xl' : 'text-4xl'
            )}
          >
            <span className="font-bold">{t.global.e_media}</span>
            <span>{t.global.content}</span>
          </div>
        </div>
      </header>
    </>
  );
}
